// Feb 2026 — Transferable Skills Reflection result page.
//
// Token URL (`/reflection/{token}`). Private — viewable only when
// the teacher comes back via the emailed link. Renders:
//   * the LLM-generated colleague-voice reflection,
//   * a clean 7-axis radar chart of the skill averages.

import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
} from "recharts";
import { Loader2, AlertCircle } from "lucide-react";
import useDocumentMeta from "../lib/useDocumentMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Shorter labels for the radar — full labels are too long to render
// around a 300px-wide chart on mobile.
const RADAR_SHORT_LABEL = {
  critical_thinking: "Critical Thinking",
  innovation: "Innovation",
  self_directed: "Self-Directed",
  collaboration: "Collaboration",
  communication: "Communication",
  global_citizenship: "Global Citizenship",
  digital_literacy: "Digital Literacy",
};

export default function ReflectionResultPage() {
  const { token } = useParams();
  useDocumentMeta({
    title: "Your transferable skills reflection — Atomic Edu",
    description: "Your year-end reflection across the seven Ontario transferable skills.",
  });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: d } = await axios.get(
          `${API}/reflection/by-token/${encodeURIComponent(token)}`,
        );
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled)
          setError(
            e?.response?.status === 404
              ? "Reflection not found — please use the link from your email."
              : "Couldn't load your reflection — please refresh.",
          );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f4f5f7] flex items-center justify-center text-[#4a6984]">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading your reflection…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#f4f5f7] px-6 py-20">
        <div
          className="max-w-md mx-auto ae-card p-7 bg-white text-center"
          data-testid="reflection-result-error"
        >
          <AlertCircle className="h-6 w-6 text-rose-600 mx-auto mb-3" />
          <h1 className="font-extrabold text-lg text-[#1c2d3d]">
            We couldn&apos;t find that reflection
          </h1>
          <p className="mt-2 text-sm text-[#4a6984]/85">{error}</p>
          <Link
            to="/reflection"
            data-testid="reflection-result-back-link"
            className="inline-block mt-4 text-sm font-extrabold text-[#1c2d3d] hover:underline"
          >
            Start a new reflection →
          </Link>
        </div>
      </div>
    );
  }

  const radarData = (data.skill_rows || []).map((row) => ({
    skill: RADAR_SHORT_LABEL[row.skill_id] || row.label,
    value: row.average,
  }));

  return (
    <div className="min-h-screen bg-[#f4f5f7]">
      <header className="bg-[#1c2d3d] text-white">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <Link to="/" className="text-sm font-extrabold tracking-tight">
            ← Atomic Edu
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 pt-10 pb-20" data-testid="reflection-result-main">
        <p className="text-[10px] uppercase tracking-[0.22em] font-extrabold text-[#4a6984]/75">
          {data.first_name ? `${data.first_name} — ` : ""}your year-end reflection
        </p>
        <h1
          data-testid="reflection-result-summary"
          className="mt-3 font-black text-2xl sm:text-3xl text-[#1c2d3d] tracking-tight leading-snug"
        >
          {data.summary || "A read on your year, and one place to anchor next year."}
        </h1>

        {/* Radar */}
        <div
          className="mt-8 ae-card p-5 sm:p-7 bg-white"
          data-testid="reflection-result-radar"
        >
          <p className="text-[10px] uppercase tracking-[0.22em] font-extrabold text-[#4a6984]/75 mb-1">
            Your shape across the seven skills
          </p>
          <p className="text-sm text-[#4a6984]/85 mb-3">
            Each axis is the average of your item ratings for that skill (1–4).
          </p>
          <div style={{ width: "100%", height: 360 }}>
            <ResponsiveContainer>
              <RadarChart
                cx="50%"
                cy="50%"
                outerRadius="78%"
                data={radarData}
              >
                <PolarGrid stroke="#cbd3da" />
                <PolarAngleAxis
                  dataKey="skill"
                  tick={{ fontSize: 11, fontWeight: 700, fill: "#1c2d3d" }}
                />
                <PolarRadiusAxis
                  angle={90}
                  domain={[0, 4]}
                  tickCount={5}
                  tick={{ fontSize: 9, fill: "#4a6984" }}
                />
                <Radar
                  name="Skill rating"
                  dataKey="value"
                  stroke="#3EB489"
                  fill="#3EB489"
                  fillOpacity={0.35}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            {(data.skill_rows || []).map((row) => (
              <div
                key={row.skill_id}
                data-testid={`reflection-skill-row-${row.skill_id}`}
                className="flex items-baseline justify-between gap-3 border-b border-[#4a6984]/10 pb-1.5"
              >
                <span className="text-[#1c2d3d] font-bold leading-tight">
                  {row.label}
                </span>
                <span className="text-[#4a6984] font-extrabold tabular-nums">
                  {row.average ? row.average.toFixed(2) : "—"}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Reflection paragraphs */}
        <article
          className="mt-8 prose prose-slate max-w-none"
          data-testid="reflection-result-paragraphs"
        >
          {(data.paragraphs || []).map((p, i) => (
            <p
              key={i}
              data-testid={`reflection-result-paragraph-${i}`}
              className="text-[#1c2d3d] text-base leading-relaxed mb-4"
            >
              {p}
            </p>
          ))}
        </article>

        <div className="mt-10 ae-card p-5 sm:p-6 bg-white" data-testid="reflection-result-footer-note">
          <p className="text-xs uppercase tracking-[0.22em] font-extrabold text-[#4a6984]/70 mb-1">
            Save this link
          </p>
          <p className="text-sm text-[#4a6984]/85 leading-relaxed">
            We&apos;ll also send this reflection back to you in mid-August and
            early September — once when planning season hits, and once when
            you&apos;re actually setting up your room.
          </p>
        </div>
      </main>
    </div>
  );
}
